/**
 * The player's own words (the views, buttons, labels) in English and Turkish:
 * ?lang=tr|en, else the browser's language, as the chat does (chat/i18n.ts).
 * Kept apart from the chat's copy so the player doesn't load the chat chunk.
 */
type Views = 'pending' | 'starting' | 'ended' | 'unavailable' | 'closed' | 'notfound' | 'offline' | 'unsupported' | 'members' | 'removed';

const EN = {
  /** eyebrow, headline (when there is no ?title), sub */
  views: {
    pending: ['Starting soon', 'The stream will begin shortly', 'This page starts playing by itself. No need to refresh.'],
    starting: ['Going live', 'Here we go', 'Connecting to the stream…'],
    ended: ['Stream ended', 'That’s a wrap', 'Thanks for watching.'],
    unavailable: ['Unavailable', 'This stream isn’t available', 'It was cancelled or never started.'],
    closed: ['Watch page closed', 'This watch page has closed', 'The stream may continue on the host’s other platforms.'],
    notfound: ['Not found', 'We couldn’t find this stream', 'Check the link you were given.'],
    offline: ['Connection', 'Can’t reach the stream', 'Retrying automatically…'],
    unsupported: ['Browser', 'This browser can’t play live video', 'Try a recent Chrome, Safari, Firefox or Edge.'],
    members: ['Members only', 'This stream is for members', 'Open it from the site where you signed in.'],
    removed: ['Access ended', 'Your access to this stream ended', 'Open it again from the site where you signed in.'],
  } as Record<Views, [string, string, string]>,
  /** Ended, with the stream's title as the headline. */
  wrapThanks: 'That’s a wrap. Thanks for watching.',
  thanks: 'Thanks for watching.',
  liveFor: (d: string) => `Live for ${d}.`,
  duration: (h: number, m: number) => (h ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`),
  // A channel between streams.
  nextStream: 'Next stream',
  offline: 'Offline',
  comingUp: 'Coming up',
  nothingOnAir: 'Nothing on air right now',
  startsBySelf: 'This page starts playing by itself.',
  startsWithNext: 'This page starts playing by itself when the next stream begins.',
  reconnecting: 'Reconnecting…',
  replay: 'Watch the replay',
  tapUnmute: 'Tap to unmute',
  chat: 'Chat',
  live: 'LIVE',
  goLive: 'GO LIVE',
  quality: 'Quality',
  auto: 'Auto',
  play: 'Play',
  pause: 'Pause',
  mute: 'Mute',
  unmute: 'Unmute',
  volume: 'Volume',
  jumpLive: 'Jump to live',
  timeLive: 'Time live',
  pip: 'Picture in picture',
  fullscreen: 'Full screen',
  /** The tab title: "<title> · Live". */
  liveTab: 'Live',
  locale: 'en-GB',
};
export type PlayerCopy = typeof EN;

const TR: PlayerCopy = {
  views: {
    pending: ['Birazdan başlıyor', 'Yayın birazdan başlayacak', 'Bu sayfa yayını kendiliğinden başlatır. Yenilemenize gerek yok.'],
    starting: ['Yayına geçiliyor', 'Başlıyoruz', 'Yayına bağlanılıyor…'],
    ended: ['Yayın sona erdi', 'Hepsi bu kadar', 'İzlediğiniz için teşekkürler.'],
    unavailable: ['Yayın yok', 'Bu yayın kullanılamıyor', 'İptal edilmiş ya da hiç başlamamış.'],
    closed: ['İzleme sayfası kapandı', 'Bu izleme sayfası kapandı', 'Yayın, yayıncının diğer platformlarında sürüyor olabilir.'],
    notfound: ['Bulunamadı', 'Bu yayını bulamadık', 'Size verilen bağlantıyı kontrol edin.'],
    offline: ['Bağlantı', 'Yayına ulaşılamıyor', 'Otomatik olarak yeniden deneniyor…'],
    unsupported: ['Tarayıcı', 'Bu tarayıcı canlı video oynatamıyor', 'Güncel bir Chrome, Safari, Firefox ya da Edge deneyin.'],
    members: ['Yalnızca üyeler', 'Bu yayın üyelere özel', 'Giriş yaptığınız siteden açın.'],
    removed: ['Erişim sona erdi', 'Bu yayına erişiminiz sona erdi', 'Giriş yaptığınız siteden yeniden açın.'],
  },
  wrapThanks: 'Hepsi bu kadar. İzlediğiniz için teşekkürler.',
  thanks: 'İzlediğiniz için teşekkürler.',
  liveFor: (d) => `Yayın süresi: ${d}.`,
  duration: (h, m) => (h ? `${h} sa ${String(m).padStart(2, '0')} dk` : `${m} dk`),
  nextStream: 'Sıradaki yayın',
  offline: 'Çevrimdışı',
  comingUp: 'Yakında',
  nothingOnAir: 'Şu an yayın yok',
  startsBySelf: 'Bu sayfa yayını kendiliğinden başlatır.',
  startsWithNext: 'Sıradaki yayın başlayınca bu sayfa kendiliğinden oynatır.',
  reconnecting: 'Yeniden bağlanılıyor…',
  replay: 'Tekrarını izle',
  tapUnmute: 'Sesi açmak için dokunun',
  chat: 'Sohbet',
  live: 'CANLI',
  goLive: 'CANLIYA DÖN',
  quality: 'Kalite',
  auto: 'Otomatik',
  play: 'Oynat',
  pause: 'Duraklat',
  mute: 'Sesi kapat',
  unmute: 'Sesi aç',
  volume: 'Ses düzeyi',
  jumpLive: 'Canlıya geç',
  timeLive: 'Yayın süresi',
  pip: 'Resim içinde resim',
  fullscreen: 'Tam ekran',
  liveTab: 'Canlı',
  locale: 'tr-TR',
};

export function playerCopy(lang: string | null): PlayerCopy {
  const l = (lang || navigator.language || 'en').toLowerCase();
  return l.startsWith('tr') ? TR : EN;
}
